import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { doc, getDoc, collection, query, where, getCountFromServer } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { converters } from "@/lib/firestore";
import { useOrgs, type OrgWithId } from "@/hooks/useOrgs";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AddOrgDialog } from "@/components/orgs/AddOrgDialog";
import { PlatformPageHeader } from "@/components/platform/PlatformPageHeader";
import { Plus, Building2, Users } from "lucide-react";

interface OwnerInfo {
  name: string;
  email: string;
}

type OrgRow = OrgWithId & { owner?: OwnerInfo; stats?: OrgStats };

function useOwnerLookup(orgs: OrgWithId[]) {
  const [owners, setOwners] = useState<Record<string, OwnerInfo>>({});

  const ownerIds = useMemo(
    () => [...new Set(orgs.map((o) => o.ownerId).filter(Boolean))] as string[],
    [orgs]
  );

  useEffect(() => {
    if (ownerIds.length === 0) return;

    // Only fetch IDs we don't already have
    const missing = ownerIds.filter((id) => !(id in owners));
    if (missing.length === 0) return;

    (async () => {
      const results = await Promise.all(
        missing.map(async (uid) => {
          const snap = await getDoc(doc(db, "orgUsers", uid).withConverter(converters.orgUsers));
          if (!snap.exists()) return null;
          const data = snap.data();
          return { uid, name: data.name || "", email: data.email || "" };
        })
      );
      const newOwners: Record<string, OwnerInfo> = {};
      for (const r of results) {
        if (r) newOwners[r.uid] = { name: r.name, email: r.email };
      }
      if (Object.keys(newOwners).length > 0) {
        setOwners((prev) => ({ ...prev, ...newOwners }));
      }
    })();
  }, [ownerIds]);

  return owners;
}

interface OrgStats {
  studioCount: number;
  userCount: number;
}

function useOrgStats(orgs: OrgWithId[]) {
  const [stats, setStats] = useState<Record<string, OrgStats>>({});

  const orgIdKey = useMemo(
    () => orgs.map((o) => o.id).sort().join(","),
    [orgs]
  );

  useEffect(() => {
    if (orgs.length === 0) return;

    (async () => {
      const results: Record<string, OrgStats> = {};
      await Promise.all(
        orgs.map(async (org) => {
          const [studioSnap, userSnap] = await Promise.all([
            getCountFromServer(
              query(collection(db, "studios"), where("orgId", "==", org.id))
            ),
            getCountFromServer(
              query(collection(db, "endUsers"), where("orgId", "==", org.id))
            ),
          ]);
          results[org.id] = {
            studioCount: studioSnap.data().count,
            userCount: userSnap.data().count,
          };
        })
      );
      setStats(results);
    })();
  }, [orgIdKey]);

  return stats;
}

const columns: ColumnDef<OrgRow, unknown>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <span className="font-medium">{row.original.name}</span>
    ),
  },
  {
    id: "owner",
    header: "Owner",
    enableSorting: false,
    cell: ({ row }) => {
      const owner = row.original.owner;
      if (!owner) {
        return (
          <span className="text-xs text-text-muted">—</span>
        );
      }
      return (
        <div className="min-w-0">
          <p className="truncate text-sm">{owner.name || owner.email}</p>
          {owner.name && (
            <p className="truncate text-xs text-text-muted">{owner.email}</p>
          )}
        </div>
      );
    },
  },
  {
    id: "studios",
    header: "Studios",
    accessorFn: (row) => row.stats?.studioCount ?? 0,
    cell: ({ row }) => {
      const count = row.original.stats?.studioCount;
      if (count === undefined) {
        return <span className="text-xs text-text-muted">...</span>;
      }
      return (
        <div className="flex items-center gap-1.5">
          <Building2 className="h-3.5 w-3.5 text-text-muted" />
          <span>{count}</span>
        </div>
      );
    },
  },
  {
    id: "users",
    header: "Users",
    accessorFn: (row) => row.stats?.userCount ?? 0,
    cell: ({ row }) => {
      const count = row.original.stats?.userCount;
      if (count === undefined) {
        return <span className="text-xs text-text-muted">...</span>;
      }
      return (
        <div className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-text-muted" />
          <span>{count}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <Badge
        variant={row.original.status === "active" ? "success" : "danger"}
      >
        {row.original.status}
      </Badge>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ row }) => {
      const ts = row.original.createdAt;
      if (!ts?.toDate) return "—";
      return ts.toDate().toLocaleDateString();
    },
  },
];

export function OrgsPage() {
  const navigate = useNavigate();
  const { orgs, loading, error, createOrg } = useOrgs();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const owners = useOwnerLookup(orgs);
  const stats = useOrgStats(orgs);

  const rows: OrgRow[] = useMemo(
    () =>
      orgs.map((org) => ({
        ...org,
        owner: org.ownerId ? owners[org.ownerId] : undefined,
        stats: stats[org.id],
      })),
    [orgs, owners, stats]
  );

  return (
    <div>
      <PlatformPageHeader
        eyebrow="Platform / Organizations"
        title="Organizations"
        subtitle="Manage client organizations and their workspaces."
        actions={
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4" />
            Add Organization
          </Button>
        }
      />

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center font-body text-sm text-text-muted">
          Loading organizations...
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          searchPlaceholder="Search organizations..."
          searchColumn="name"
          initialSorting={[{ id: "name", desc: false }]}
          onRowClick={(org) => navigate(`/admin/orgs/${org.id}`)}
        />
      )}

      <AddOrgDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={createOrg}
      />
    </div>
  );
}
